import { PulseLoader, Spacer } from '@sparemin/blockhead';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'components/Modal/v2';
import usePrevious from 'hooks/usePrevious';
import { hideModal } from 'redux/modules/modal';
import ProjectPreviewModalActions from './ProjectPreviewModalActions';
import {
  useFileArchiveInfo,
  useIsLoading,
  useIsRegeneratingProject,
  useIsStatusNullOrUndefined,
  useProjectWidgetId,
  useVideoUrl,
} from './ProjectPreviewModalContext';
import ProjectPreviewModalHeading from './ProjectPreviewModalHeading';
import ProjectPreviewModalVideo from './ProjectPreviewModalVideo';
import ProjectPreviewModalVideoStatus from './ProjectPreviewModalVideoStatus';
import { ProjectPreviewModalAction } from './types';
import { block } from './utils';

export interface ProjectPreviewModalContentsProps {
  onRegenerationFinished: () => Promise<void>;
}

const ProjectPreviewModalContents: React.FC<ProjectPreviewModalContentsProps> = ({
  onRegenerationFinished,
}) => {
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

  const widgetId = useProjectWidgetId();
  const isRegeneratingProject = useIsRegeneratingProject();
  const isLoading = useIsLoading();
  const isStatusNullOrUndefined = useIsStatusNullOrUndefined();
  const videoUrl = useVideoUrl();
  const { isFileArchived, isRestoreInProgress } = useFileArchiveInfo();

  const dispatch = useDispatch();

  const prevIsRegeneratingProject = usePrevious(isRegeneratingProject);

  const handleAction = useCallback(
    (action: ProjectPreviewModalAction): void => {
      dispatch(hideModal(action));
    },
    [dispatch],
  );

  const handleRegenerationFinished = useCallback(async (): Promise<void> => {
    try {
      setIsUpdatingConfig(true);

      await onRegenerationFinished();
    } finally {
      setIsUpdatingConfig(false);
    }
  }, [onRegenerationFinished, setIsUpdatingConfig]);

  useEffect(() => {
    if (prevIsRegeneratingProject && !isRegeneratingProject) {
      handleRegenerationFinished();
    }
  }, [
    handleRegenerationFinished,
    isRegeneratingProject,
    prevIsRegeneratingProject,
  ]);

  return (
    <Modal
      className={block('contents')}
      bodyClassName={block('preview-body')}
      headerClassName={block('preview-header')}
      footer={() => null}
    >
      {isLoading || isUpdatingConfig ? (
        <PulseLoader />
      ) : (
        <>
          {!isFileArchived &&
          !isRestoreInProgress &&
          !isRegeneratingProject &&
          widgetId &&
          (!isStatusNullOrUndefined || videoUrl) ? (
            <ProjectPreviewModalVideo />
          ) : (
            <ProjectPreviewModalVideoStatus />
          )}

          <Spacer
            space="16px"
            orientation="horizontal"
            align="center"
            className={block('footer')}
          >
            <ProjectPreviewModalHeading />

            <ProjectPreviewModalActions onAction={handleAction} />
          </Spacer>
        </>
      )}
    </Modal>
  );
};

export default ProjectPreviewModalContents;
